import { useEffect, useState } from 'react'
import Icon from '../components/Icon'
import ConfirmDialog from '../components/ConfirmDialog'
import { resolveAccent } from '../components/accentColors'
import { fetchInsightSettings, saveInsightSelection } from '../api/insights'
import InsightRunControl from './InsightRunControl'
import styles from './AdminInsightsTab.module.css'

/**
 * AdminInsightsTab — Insight Selection: pick up to `maxSelected` insights to
 * surface on the workspace Control Panel. Backed by gaussiq-intelligence's
 * /insights/selection (the global insights_catalog marked with this
 * tenant's insights_jobs selection) — see src/api/insights.js. A job is keyed by
 * its stable job_key (insight.id); the saved order becomes its display_order.
 * The library the tenant sees is scoped server-side to the insights it may select
 * (global plus any granted to its UUID), so nothing here needs to filter it.
 *
 * The tenant's own UUID comes back with the settings and is shown as a footnote —
 * an admin quotes it when requesting a custom insight built for them.
 *
 * Selection is drafted locally and only persisted on "Save changes". Because
 * removing a previously-enabled job deletes its Control Panel run history
 * server-side, saving a draft that drops one shows a confirm-and-warn dialog
 * first (see ConfirmDialog usage below).
 *
 * A superadmin also gets a "Run now" control on each SAVED slot (InsightRunControl).
 * Saved, not drafted: a run stores into the tenant's persisted selection, so a slot
 * added but not yet saved has nothing to run against.
 */
export default function AdminInsightsTab({ isSuperAdmin = false }) {
  const [jobs, setJobs] = useState([])
  const [maxSelected, setMaxSelected] = useState(4)
  const [savedIds, setSavedIds] = useState([])
  const [selectedIds, setSelectedIds] = useState([])
  const [tenantUuid, setTenantUuid] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [pendingRemovalWarning, setPendingRemovalWarning] = useState(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    let cancelled = false
    fetchInsightSettings()
      .then(({ jobs: fetchedJobs, maxSelected: max, tenantUuid: uuid }) => {
        if (cancelled) return
        const enabledIds = fetchedJobs
          .filter(j => j.enabled)
          .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
          .map(j => j.id)
        setJobs(fetchedJobs)
        setMaxSelected(max ?? 4)
        setSavedIds(enabledIds)
        setSelectedIds(enabledIds)
        setTenantUuid(uuid ?? null)
      })
      .catch(err => { if (!cancelled) setError(err.message) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  const atMax = selectedIds.length >= maxSelected
  const dirty = JSON.stringify(savedIds) !== JSON.stringify(selectedIds)

  function toggle(id) {
    setSelectedIds(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id)
      if (prev.length >= maxSelected) return prev
      return [...prev, id]
    })
  }

  function jobsById(ids) {
    return ids.map(id => jobs.find(j => j.id === id)).filter(Boolean)
  }

  async function persist() {
    setSaving(true)
    setError(null)
    try {
      // selectedIds are job_keys, already in the admin's chosen order — the
      // saved position becomes each insight's display_order server-side.
      const { jobs: freshJobs } = await saveInsightSelection(selectedIds)
      const enabledIds = freshJobs
        .filter(j => j.enabled)
        .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
        .map(j => j.id)
      setJobs(freshJobs)
      setSavedIds(enabledIds)
      setSelectedIds(enabledIds)
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  function handleSaveClick() {
    const removed = jobsById(savedIds.filter(id => !selectedIds.includes(id)))
    if (removed.length > 0) {
      setPendingRemovalWarning(removed)
      return
    }
    persist()
  }

  function handleDiscard() {
    setSelectedIds(savedIds)
  }

  function handleCancelRemoval() {
    setSelectedIds(prev => {
      const restoredIds = pendingRemovalWarning.map(j => j.id)
      const merged = [...prev, ...restoredIds.filter(id => !prev.includes(id))]
      return savedIds.filter(id => merged.includes(id)).concat(merged.filter(id => !savedIds.includes(id)))
    })
    setPendingRemovalWarning(null)
  }

  async function copyUuid() {
    try {
      await navigator.clipboard.writeText(tenantUuid)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard denied (insecure context / permissions) — the ID is still
      // visible on screen for the admin to copy manually.
    }
  }

  const selectedInsights = jobsById(selectedIds)
  const emptySlots = maxSelected - selectedInsights.length

  if (loading) {
    return <div className={styles.wrap}><div className={`${styles.panel} ${styles.loadingText}`}>Loading…</div></div>
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.panel}>
        <div className={styles.panelHeader}>
          <div>
            <h2 className={styles.panelTitle}>Control Panel insights</h2>
            <p className={styles.panelSub}>Choose up to {maxSelected} insights to appear on the workspace Control Panel.</p>
          </div>
          <span className={styles.countBadge}>{selectedIds.length} of {maxSelected} selected</span>
        </div>

        <div className={styles.slots}>
          {selectedInsights.map(insight => {
            const { text, bg, border } = resolveAccent(insight.accent)
            return (
              <div key={insight.id} className={styles.slotCard} style={{ backgroundColor: bg, borderColor: border }}>
                <button
                  type="button"
                  className={styles.slotRemove}
                  onClick={() => toggle(insight.id)}
                  aria-label={`Remove ${insight.title} from Control Panel`}
                >
                  <Icon name="close" />
                </button>
                <span className={styles.slotIcon} style={{ color: text }}><Icon name={insight.icon} /></span>
                <span className={styles.slotTitle}>{insight.title}</span>
                <span className={styles.slotDescription}>{insight.description}</span>
                {isSuperAdmin && savedIds.includes(insight.id) && (
                  <InsightRunControl jobKey={insight.id} title={insight.title} />
                )}
              </div>
            )
          })}
          {Array.from({ length: Math.max(0, emptySlots) }).map((_, i) => (
            <div key={`empty-${i}`} className={styles.emptySlot}>
              <span className={styles.emptySlotNumber}>{selectedInsights.length + i + 1}</span>
              <span className={styles.emptySlotLabel}>Empty slot</span>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.libraryHeader}>
        <h3 className={styles.libraryTitle}>Insight Library</h3>
        <span className={styles.libraryCount}>{jobs.length} available</span>
      </div>

      <div className={styles.libraryGrid}>
        {jobs.map(insight => {
          const isSelected = selectedIds.includes(insight.id)
          const { text, bg, border } = resolveAccent(insight.accent)
          return (
            <div
              key={insight.id}
              className={styles.libraryCard}
              style={isSelected ? { backgroundColor: bg, borderColor: border } : undefined}
            >
              <span className={styles.libraryIcon} style={{ color: text, borderColor: border }}>
                <Icon name={insight.icon} />
              </span>
              <div className={styles.libraryBody}>
                <span className={styles.libraryCardTitle}>{insight.title}</span>
                <p className={styles.libraryDescription}>{insight.description}</p>
              </div>
              {!isSelected && (
                <button
                  type="button"
                  className={styles.addBtn}
                  onClick={() => toggle(insight.id)}
                  disabled={atMax}
                  title={atMax ? 'Remove an insight to add another' : undefined}
                >
                  Add
                </button>
              )}
            </div>
          )
        })}
      </div>

      <div className={styles.footer}>
        <span className={styles.footerStatus}>
          {error ? <span className={styles.footerError}>{error}</span>
            : saving ? 'Saving…'
            : dirty ? 'Unsaved changes' : 'All changes saved'}
        </span>
        <div className={styles.footerActions}>
          <button type="button" className={styles.resetBtn} onClick={handleDiscard} disabled={!dirty || saving}>
            Discard changes
          </button>
          <button type="button" className={styles.saveBtn} onClick={handleSaveClick} disabled={!dirty || saving}>
            Save changes
          </button>
        </div>
      </div>

      {tenantUuid && (
        <div className={styles.tenantId}>
          <div className={styles.tenantIdText}>
            <span className={styles.tenantIdLabel}>Your tenant ID</span>
            <span className={styles.tenantIdHint}>Provide this when requesting a custom insight.</span>
          </div>
          <code className={styles.tenantIdValue}>{tenantUuid}</code>
          <button type="button" className={styles.copyBtn} onClick={copyUuid}>
            {copied && <Icon name="check" />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      )}

      {pendingRemovalWarning && (
        <ConfirmDialog
          title="Remove insight from Control Panel"
          message={
            `Removing ${pendingRemovalWarning.map(j => j.title).join(', ')} will permanently delete `
            + `${pendingRemovalWarning.length === 1 ? 'its' : 'their'} historical Control Panel data. `
            + 'This cannot be undone. Continue?'
          }
          confirmLabel="Remove Insight"
          confirmIcon="trash"
          variant="danger"
          icon="warning"
          onConfirm={() => { setPendingRemovalWarning(null); persist() }}
          onCancel={handleCancelRemoval}
        />
      )}
    </div>
  )
}
